import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

const DEFAULT_FORM_FIELDS = [
  { name: 'firstName', label: 'First name', required: true, type: 'text' },
  { name: 'middleName', label: 'Middle name', required: false, type: 'text' },
  { name: 'lastName', label: 'Last name', required: true, type: 'text' },
  { name: 'dateOfBirth', label: 'Date of birth', required: true, type: 'text', placeholder: 'MM/DD/YYYY' },
  { name: 'mrn', label: 'MRN', required: false, type: 'text' },
  {
    name: 'sex',
    label: 'Sex',
    required: true,
    type: 'search-select',
    options: [
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' }
    ]
  },
  { name: 'email', label: 'Email', required: true, type: 'email' },
  { name: 'primaryNumber', label: 'Phone', required: true, type: 'tel', placeholder: 'XXX-XXX-XXXX' },
  {
    name: 'address',
    label: 'Address',
    required: true,
    type: 'text',
    placeholder: '123 Example Dr, Dallas, Texas 75082'
  },
  { name: 'area', label: 'Area', required: true, type: 'text', placeholder: 'Florida' },
  { name: 'preferredLanguage', label: 'Preferred language', required: false, type: 'text' },
  { name: 'mainReasonForReferral', label: 'Referral reason', required: false, type: 'text' },
  { name: 'referringOrganization', label: 'Referring organization', required: false, type: 'text' }
];

const STATE_ABBREVIATIONS = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia'
};

function normalize(value) {
  return String(value || '').trim();
}

function inferAreaFromAddress(address) {
  const value = normalize(address);
  if (!value) return '';
  const tokens = value.split(/\s+/).map((token) => token.replace(/[,.]/g, '')).filter(Boolean);
  if (tokens.length === 0) return '';
  let candidate = tokens[tokens.length - 1].toUpperCase();
  if (/^\d+$/.test(candidate) && tokens.length > 1) {
    candidate = tokens[tokens.length - 2].toUpperCase();
  }
  return STATE_ABBREVIATIONS[candidate] || '';
}

function formatDateInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatPhoneInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function toBase64Utf8(value) {
  if (typeof TextEncoder !== 'undefined') {
    const encoded = new TextEncoder().encode(value);
    let binary = '';
    encoded.forEach((charCode) => {
      binary += String.fromCharCode(charCode);
    });
    return window.btoa(binary);
  }

  const encoded = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hexByte) => String.fromCharCode(parseInt(hexByte, 16))
  );
  return window.btoa(encoded);
}

function normalizeOptions(options) {
  const normalized = [];
  const seen = new Set();
  (options || []).forEach((option) => {
    if (option && typeof option === 'object') {
      const label = normalize(option.label);
      const value = normalize(option.value);
      if (!label || !value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push({ label, value });
      return;
    }
    const raw = normalize(option);
    if (!raw) return;
    const key = raw.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ label: raw, value: raw });
  });
  return normalized.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function filterOptions(options, inputValue) {
  const query = normalize(inputValue).toLowerCase();
  if (!query) return options;
  return options.filter((option) => (
    normalize(option.label).toLowerCase().includes(query)
    || normalize(option.value).toLowerCase().includes(query)
  ));
}

function findMatchingOption(rawValue, options) {
  const value = normalize(rawValue).toLowerCase();
  if (!value) return null;
  return options.find(
    (option) => normalize(option.value).toLowerCase() === value || normalize(option.label).toLowerCase() === value
  ) || null;
}

function buildDisplayValues(fields, values) {
  const display = {};
  fields.forEach((field) => {
    const name = field.name;
    const raw = normalize(values[name]);
    if (!raw) {
      display[name] = '';
      return;
    }
    if (field.type === 'search-select') {
      const options = normalizeOptions(field.options);
      const match = findMatchingOption(raw, options);
      display[name] = match ? match.label : raw;
      return;
    }
    display[name] = raw;
  });
  return display;
}

function validateForm(fields, values) {
  const errors = {};
  (fields || []).forEach((field) => {
    if (!field?.name) return;
    const current = normalize(values[field.name]);
    if (field.required && !current) {
      errors[field.name] = 'Required';
      return;
    }
    if (field.type === 'search-select' && current) {
      const options = normalizeOptions(field.options);
      if (!findMatchingOption(current, options)) {
        errors[field.name] = 'Select a valid option';
      }
    }
  });

  const email = normalize(values.email);
  if (email && (!email.includes('@') || !email.split('@').pop().includes('.'))) {
    errors.email = 'Enter a valid email address';
  }

  const dob = normalize(values.dateOfBirth);
  if (dob && !/^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/.test(dob)) {
    errors.dateOfBirth = 'Date of birth must use MM/DD/YYYY';
  }

  const phone = normalize(values.primaryNumber);
  if (phone && !/^\d{3}-\d{3}-\d{4}$/.test(phone)) {
    errors.primaryNumber = 'Phone must use XXX-XXX-XXXX';
  }

  const sex = normalize(values.sex).toLowerCase();
  if (sex && !['male', 'female'].includes(sex)) {
    errors.sex = 'Select Male or Female';
  }

  const mrn = normalize(values.mrn);
  if (mrn && !/^\d{7}$/.test(mrn)) {
    errors.mrn = 'MRN must be 7 digits';
  }

  return errors;
}

function computeDelta(initialValues, currentValues) {
  const delta = {};
  Object.keys(currentValues || {}).forEach((key) => {
    const before = normalize(initialValues?.[key]);
    const after = normalize(currentValues?.[key]);
    if (before !== after) {
      delta[key] = after;
    }
  });
  return delta;
}

function PatientCreateFormCard({ data, conversationId }) {
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const formFromCard = data.form || {};
  const formFromConfirmPayload = data.confirm?.action?.payload || {};
  const parsedFields = Array.isArray(formFromCard.fields)
    ? formFromCard.fields
    : (Array.isArray(formFromConfirmPayload.fields) ? formFromConfirmPayload.fields : []);
  const fields = parsedFields.length > 0 ? parsedFields : DEFAULT_FORM_FIELDS;

  const initialValues = useMemo(() => {
    const source = {
      ...(formFromCard.values || {}),
      ...(formFromConfirmPayload.values || {})
    };
    const normalized = {};
    fields.forEach((field) => {
      const fieldName = field.name;
      const raw = normalize(source[fieldName]);
      if (fieldName === 'dateOfBirth') {
        normalized[fieldName] = formatDateInput(raw);
        return;
      }
      if (fieldName === 'primaryNumber') {
        normalized[fieldName] = formatPhoneInput(raw);
        return;
      }
      normalized[fieldName] = raw;
    });
    return normalized;
  }, [fields, formFromCard.values, formFromConfirmPayload.values]);

  const [values, setValues] = useState(initialValues);
  const [displayValues, setDisplayValues] = useState(buildDisplayValues(fields, initialValues));
  const [errors, setErrors] = useState({ ...(formFromCard.errors || formFromConfirmPayload.errors || {}) });
  const [submitState, setSubmitState] = useState('idle');
  const [cancelState, setCancelState] = useState('idle');
  const [areaManuallyOverridden, setAreaManuallyOverridden] = useState(false);
  const [openSelectName, setOpenSelectName] = useState('');
  const cardRef = useRef(null);

  const pendingActionId = formFromCard.pendingActionId
    || formFromConfirmPayload.pendingActionId
    || data.submit?.action?.payload?.pendingActionId
    || data.cancel?.action?.payload?.pendingActionId
    || '';
  const formMode = String(formFromCard.mode || formFromConfirmPayload.mode || 'create').toLowerCase() === 'update'
    ? 'update'
    : 'create';

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setOpenSelectName('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const setFieldValue = (fieldName, nextValue) => {
    setValues((prev) => {
      const updated = { ...prev, [fieldName]: normalize(nextValue) };
      if (fieldName === 'address' && !areaManuallyOverridden) {
        const inferred = inferAreaFromAddress(nextValue);
        if (inferred) {
          updated.area = inferred;
          setDisplayValues((prevDisplay) => ({ ...prevDisplay, area: inferred }));
        }
      }
      return updated;
    });

    if (fieldName === 'area') {
      setAreaManuallyOverridden(true);
    }

    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleBasicInput = (fieldName, inputValue) => {
    let nextValue = inputValue;
    if (fieldName === 'dateOfBirth') {
      nextValue = formatDateInput(inputValue);
    } else if (fieldName === 'primaryNumber') {
      nextValue = formatPhoneInput(inputValue);
    }
    setDisplayValues((prev) => ({ ...prev, [fieldName]: nextValue }));
    setFieldValue(fieldName, nextValue);
  };

  const handleSearchInput = (field, inputValue) => {
    const fieldName = field.name;
    const options = normalizeOptions(field.options);
    setDisplayValues((prev) => ({ ...prev, [fieldName]: inputValue }));
    const match = findMatchingOption(inputValue, options);
    setFieldValue(fieldName, match ? match.value : inputValue);
    setOpenSelectName(fieldName);
  };

  const selectOption = (field, option) => {
    setDisplayValues((prev) => ({ ...prev, [field.name]: option.label }));
    setFieldValue(field.name, option.value);
    setOpenSelectName('');
  };

  const onSubmit = async () => {
    if (!conversationId || !pendingActionId || submitState === 'loading' || submitState === 'sent') return;

    const validationErrors = validateForm(fields, values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitState('error');
      return;
    }

    const delta = computeDelta(initialValues, values);
    const payloadB64 = toBase64Utf8(JSON.stringify(delta));

    setSubmitState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: formMode === 'update'
            ? `/submit-update-patient ${pendingActionId} ${payloadB64}`
            : `/submit-create-patient ${pendingActionId} ${payloadB64}`
        }
      });
      setSubmitState('sent');
      setErrors({});
    } catch (error) {
      console.error('Failed to submit patient create form:', error);
      setSubmitState('error');
    }
  };

  const onCancel = async () => {
    if (!conversationId || !pendingActionId || cancelState === 'loading' || cancelState === 'sent') return;
    setCancelState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: `/discard ${pendingActionId}`
        }
      });
      setCancelState('sent');
    } catch (error) {
      console.error('Failed to cancel patient create form:', error);
      setCancelState('idle');
    }
  };

  return (
    <div className="widget-card widget-card-form" ref={cardRef}>
      <div className="widget-card-status-header widget-header-warning">
        <span className="widget-status-dot" />
        <span className="widget-status-text">{data.status?.text || 'Form required'}</span>
      </div>

      <div className="widget-card-content">
        {(data.children || []).map((child, index) => {
          if (child.type === 'Title') {
            return <h4 key={index} className="widget-card-title">{child.value}</h4>;
          }
          if (child.type === 'Text') {
            return <p key={index} className="widget-text">{child.value}</p>;
          }
          if (child.type === 'Caption') {
            return <p key={index} className="widget-caption">{child.value}</p>;
          }
          if (child.type === 'Divider') {
            return <hr key={index} className="widget-divider" />;
          }
          return null;
        })}

        <div className="widget-form-grid">
          {fields.map((field) => {
            const fieldName = field.name;
            const value = normalize(values[fieldName]);
            const shownValue = String(displayValues[fieldName] ?? value);
            const hasError = Boolean(errors[fieldName]);
            const label = `${field.label}${field.required ? ' *' : ''}`;
            const placeholder = field.placeholder || '';

            if (field.type === 'search-select') {
              const options = normalizeOptions(field.options);
              const filteredOptions = filterOptions(options, shownValue);
              return (
                <label key={fieldName} className="widget-form-field">
                  <span className="widget-form-label">{label}</span>
                  <div className="widget-search-select">
                    <div className="widget-search-select-input-wrap">
                      <input
                        type="text"
                        value={shownValue}
                        placeholder={placeholder}
                        onFocus={() => setOpenSelectName(fieldName)}
                        onChange={(event) => handleSearchInput(field, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            setOpenSelectName('');
                          }
                          if (event.key === 'ArrowDown') {
                            setOpenSelectName(fieldName);
                          }
                        }}
                        className={`widget-form-input widget-search-select-input ${hasError ? 'has-error' : ''}`}
                      />
                      <span className="widget-search-select-caret" aria-hidden="true">v</span>
                    </div>
                    {openSelectName === fieldName && filteredOptions.length > 0 && (
                      <div className="widget-search-select-menu" role="listbox" aria-label={`${field.label} options`}>
                        {filteredOptions.map((option) => (
                          <button
                            key={`${fieldName}-${option.value}`}
                            type="button"
                            className="widget-search-select-option"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption(field, option)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasError && errors[fieldName] !== 'Required' && (
                    <span className="widget-form-error">{errors[fieldName]}</span>
                  )}
                </label>
              );
            }

            return (
              <label key={fieldName} className="widget-form-field">
                <span className="widget-form-label">{label}</span>
                <input
                  type={field.type || 'text'}
                  value={shownValue}
                  placeholder={placeholder}
                  onChange={(event) => handleBasicInput(fieldName, event.target.value)}
                  className={`widget-form-input ${hasError ? 'has-error' : ''}`}
                />
                {hasError && errors[fieldName] !== 'Required' && (
                  <span className="widget-form-error">{errors[fieldName]}</span>
                )}
              </label>
            );
          })}
        </div>

        <div className="widget-card-actions">
          <button
            type="button"
            className={`widget-btn widget-btn-confirm ${submitState === 'loading' ? 'is-loading' : ''} ${submitState === 'sent' ? 'is-done' : ''}`}
            onClick={onSubmit}
            disabled={!conversationId || !pendingActionId || submitState === 'loading' || submitState === 'sent'}
          >
            {submitState === 'loading' && <span className="widget-btn-spinner" aria-hidden="true" />}
            {submitState === 'loading' ? 'Submitting...' : submitState === 'sent' ? 'Submitted' : (data.submit?.label || 'Submit details')}
          </button>
          <button
            type="button"
            className={`widget-btn widget-btn-cancel ${cancelState === 'loading' ? 'is-loading' : ''} ${cancelState === 'sent' ? 'is-done' : ''}`}
            onClick={onCancel}
            disabled={!conversationId || !pendingActionId || cancelState === 'loading' || cancelState === 'sent'}
          >
            {cancelState === 'loading' ? 'Canceling...' : cancelState === 'sent' ? 'Canceled' : (data.cancel?.label || 'Cancel')}
          </button>
        </div>

        {submitState === 'error' && (
          <p className="widget-caption">Please correct the highlighted fields and try again.</p>
        )}
        {typeof errors._form === 'string' && errors._form.trim() && (
          <p className="widget-caption">{errors._form}</p>
        )}
        {submitState === 'sent' && (
          <p className="widget-caption widget-responded">Submitted. I’m validating the details.</p>
        )}
      </div>
    </div>
  );
}

export default PatientCreateFormCard;
